import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { configureAnalytics, initGoogleAnalytics, trackEvent, trackPageView, updateConsentMode } from "../../services";
import { useCookieConsent } from "../utils/useCookieConsent.jsx";
import { PUBLIC_ROUTES } from "../../constants/routes";

const PUBLIC_PATH_SET = new Set(Object.values(PUBLIC_ROUTES));

/**
 * useGoogleAnalytics Hook
 *
 * Loads Google Analytics (GA4) with Consent Mode v2 and wires consent decisions
 * from the cookie banner. Tracking is restricted to public pages only.
 *
 * @param {Object} options - Hook options
 * @param {string} [options.measurementId] - Optional GA4 measurement ID (defaults to VITE_GA_MEASUREMENT_ID)
 */
export const useGoogleAnalytics = ({ measurementId } = {}) => {
  const location = useLocation();
  const { consent } = useCookieConsent();

  const gaId = useMemo(() => measurementId || import.meta.env.VITE_GA_MEASUREMENT_ID, [measurementId]);
  const lastConsentRef = useRef(null);
  const lastPathRef = useRef("");

  // Initialize GA and sync consent mode
  useEffect(() => {
    if (!gaId) return;

    const isPublicPage = PUBLIC_PATH_SET.has(location.pathname);
    if (!isPublicPage) return;

    // Do nothing while awaiting user choice
    if (consent === null) return;

    // If denied, still initialize GA with default denied consent.
    if (consent === false) {
      initGoogleAnalytics(gaId);
      updateConsentMode(false);
      if (lastConsentRef.current !== "denied") {
        trackEvent("cookie_consent", { status: "denied" });
        lastConsentRef.current = "denied";
      }
      return;
    }

    // Consent granted: load GA, update consent, and configure
    initGoogleAnalytics(gaId);
    updateConsentMode(true);
    configureAnalytics(gaId);

    if (lastConsentRef.current !== "granted") {
      trackEvent("cookie_consent", { status: "granted" });
      lastConsentRef.current = "granted";
    }
  }, [consent, gaId, location.pathname]);

  // Track page views on public pages after consent
  useEffect(() => {
    if (!gaId || consent !== true) return;
    if (!PUBLIC_PATH_SET.has(location.pathname)) return;

    const path = `${location.pathname}${location.search || ""}`;
    if (lastPathRef.current === path) return;

    trackPageView(gaId, { path, title: document.title });
    lastPathRef.current = path;
  }, [consent, gaId, location.pathname, location.search]);
};

export default useGoogleAnalytics;
